import type { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isSectionAllowed, sectionKeyForPath } from "../lib/menuSections";
import AdminLayout from "./AdminLayout";

function AccessDenied() {
  return (
    <div className="max-w-xl mx-auto">
      <div className="bg-background-50 rounded-lg border border-background-200/60 p-8 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 text-accent-800 flex items-center justify-center">
          <i className="ri-lock-2-line text-2xl" />
        </div>
        <h1 className="font-heading text-xl font-bold text-foreground-900">Acesso restrito</h1>
        <p className="text-sm text-foreground-600 mt-2">
          Você não tem permissão para abrir esta seção do painel. Se precisar de acesso, fale com o
          administrador geral.
        </p>
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 mt-6 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line" />
          </div>
          Voltar ao Dashboard
        </Link>
      </div>
    </div>
  );
}

export default function AuthGuard({ children }: { children: ReactNode }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-100">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          <p className="text-sm text-foreground-600">Carregando…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  // Bloqueio por rota: além de esconder do menu, quem digitar a URL
  // diretamente também não consegue abrir uma seção não liberada.
  const sectionKey = sectionKeyForPath(location.pathname);
  const allowed =
    sectionKey === null || isSectionAllowed(user.role, user.permissions, sectionKey);

  return (
    <AdminLayout user={user} onLogout={logout}>
      {allowed ? children : <AccessDenied />}
    </AdminLayout>
  );
}